import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { useHasPermission } from '@/hooks/usePermissions';
import { EmailTemplateBuilderEditor } from '@/components/documents/emails/EmailTemplateBuilderEditor';
import {
  useEmailTemplate,
  useUpdateEmailTemplate,
  usePublishEmailTemplate,
} from '@/hooks/emailTemplates';

export const Route = createFileRoute('/_dashboard/dashboard/settings/email-templates/$id')({
  beforeLoad: () => {
    const { permissions } = useAuthStore.getState();
    if (permissions.length > 0 && !permissions.includes('estimates:view')) {
      throw redirect({ to: '/dashboard/home' });
    }
  },
  component: EditEmailTemplatePage,
});

function EditEmailTemplatePage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const canEdit = useHasPermission('estimates:edit');
  const { data: tpl, isLoading, isError } = useEmailTemplate(id);
  const update = useUpdateEmailTemplate();
  const publish = usePublishEmailTemplate();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (isError || !tpl) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center text-sm text-muted-foreground">
        This email could not be loaded.
      </div>
    );
  }

  return (
    <EmailTemplateBuilderEditor
      mode="edit"
      kind={tpl.kind}
      initialName={tpl.name}
      initialSubject={tpl.subject}
      initialLayout={tpl.layout}
      initialBranding={tpl.branding}
      initialBodyHtml={tpl.bodyHtml}
      status={tpl.status}
      isDefault={tpl.isDefault}
      hasUnpublishedChanges={tpl.hasUnpublishedChanges}
      readOnly={!canEdit}
      saving={update.isPending}
      publishing={publish.isPending}
      onBack={() =>
        navigate({
          to: '/dashboard/settings/document-templates',
          search: { tab: 'emails', kind: tpl.kind },
        })
      }
      onKindChange={(next) =>
        navigate({
          to: '/dashboard/settings/document-templates',
          search: { tab: 'emails', kind: next },
        })
      }
      onSave={
        canEdit
          ? (name, subject, bodyHtml, layout, branding) =>
              update.mutateAsync({ id, dto: { name, subject, bodyHtml, layout, branding } })
          : () => undefined
      }
      onPublish={canEdit ? () => publish.mutate({ id }) : undefined}
      onUseInBu={() => navigate({ to: '/dashboard/settings/trades' })}
    />
  );
}
